import {
  DetailHeader,
  type DetailHeaderAction,
  EmptyState,
  ErrorState,
  LoadingState,
} from "@elyxeer/ui";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { NotificationCard } from "~/components/notifications/NotificationCard";
import { NotificationPermissionPrompt } from "~/components/notifications/NotificationPermissionPrompt";
import { Screen } from "~/components/ui/Screen";
import { useLocale } from "~/i18n";
import { track } from "~/lib/analytics";
import { fetchNotifications, markNotificationsRead } from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import { useAuthStore } from "~/store/auth-store";
import { AnalyticsEventName } from "~/types/analytics";
import type {
  NotificationItem,
  NotificationPage,
  NotificationUnreadCount,
} from "~/types/notification";

/**
 * How many reminders one page holds.
 *
 * The API trims the feed to a fortnight and drops events that are already over, so
 * the list is short by construction and a single page is the whole inbox. There is
 * no "load more" because there is nothing older to load.
 */
const PAGE_SIZE = 20;

/**
 * The notification centre: every reminder this member has been sent, newest first.
 *
 * Opening the screen *is* the read receipt — a member who is looking at the list has
 * seen it, and asking them to press something afterwards would leave a badge up over
 * notifications they have already read. The explicit "mark everything as read" in the
 * header is therefore a repair tool, not the normal path.
 *
 * Not `NotificationsRoute`: that name belongs to the marketing-consent screen under
 * `/profile`.
 */
export function NotificationsCenterRoute() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const queryClient = useQueryClient();
  const userId = useAuthStore((state) => state.user?.id) ?? "";

  const feedKey = queryKeys.notifications(userId, locale);
  const unreadKey = queryKeys.notificationsUnread(userId, locale);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: feedKey,
    queryFn: ({ signal }) =>
      fetchNotifications({ locale, limit: PAGE_SIZE, offset: 0, signal }),
    enabled: userId.length > 0,
    // The badge that brought the member here is a minute old at most; the list it
    // links to must not be older than that.
    staleTime: 0,
    refetchOnMount: "always",
  });

  const { mutate: markRead } = useMutation({
    mutationFn: markNotificationsRead,
    onSuccess: () => {
      // Written into the cache rather than refetched: the server has already moved
      // the watermark, and a round trip would leave the dots on screen long enough
      // to be seen going out one by one.
      queryClient.setQueryData<NotificationPage>(feedKey, (current) =>
        current
          ? {
              ...current,
              unread_count: 0,
              items: current.items.map((item) =>
                item.is_unread ? { ...item, is_unread: false } : item,
              ),
            }
          : current,
      );
      queryClient.setQueryData<NotificationUnreadCount>(unreadKey, {
        unread_count: 0,
      });
    },
  });

  useEffect(() => {
    track(AnalyticsEventName.NotificationsOpen, {
      subjectType: "notification",
    });
  }, []);

  // One attempt per distinct batch of unread rows. Without the marker the mutation's
  // own cache write would settle the query, re-run the effect and post again.
  const attemptedReadMarker = useRef<string | null>(null);
  const readMarker =
    data && data.unread_count > 0
      ? (data.items.find((item) => item.is_unread)?.id ?? String(data.total))
      : null;

  useEffect(() => {
    if (!readMarker || attemptedReadMarker.current === readMarker) return;
    attemptedReadMarker.current = readMarker;
    markRead();
  }, [markRead, readMarker]);

  const hasUnread = (data?.unread_count ?? 0) > 0;

  const actions = useMemo(
    (): DetailHeaderAction[] =>
      hasUnread
        ? [
            {
              id: "mark-read",
              // Named explicitly: the derived id would be `notifications.menu-mark-read`,
              // and the control the plan (and any e2e) reaches for is `notifications.mark-read`.
              testId: "notifications.mark-read",
              label: t("notifications.markAllRead"),
              icon: <DoneAllOutlinedIcon fontSize="small" />,
              onSelect: () => markRead(),
            },
          ]
        : [],
    [hasUnread, markRead, t],
  );

  const header = (
    <DetailHeader
      backTo="/news"
      backLabel={t("notifications.backToDashboard")}
      backTestId="notifications.back"
      actions={actions}
      menuLabel={t("common.moreActions")}
      menuTestId="notifications.menu"
    />
  );

  const openItem = (item: NotificationItem) => {
    track(AnalyticsEventName.NotificationClick, {
      subjectType: "notification",
      subjectId: item.id,
      subjectKey: item.event_slug,
      props: { kind: item.kind },
    });
  };

  const items = data?.items ?? [];

  return (
    <Screen maxWidth="md">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: 3, md: 4 },
        }}
      >
        {header}

        <Typography
          variant="H1"
          sx={{ color: "text.primary" }}
          data-testid="notifications.title"
        >
          {t("notifications.title")}
        </Typography>

        <NotificationPermissionPrompt />

        {isPending ? <LoadingState /> : null}

        {isError ? (
          <ErrorState
            message={
              error instanceof Error ? error.message : t("notifications.error")
            }
            onRetry={() => void refetch()}
          />
        ) : null}

        {!isPending && !isError && items.length === 0 ? (
          <EmptyState message={t("notifications.empty")} />
        ) : null}

        {items.length > 0 ? (
          <Box
            data-testid="notifications.list"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1.5, md: 2 },
            }}
          >
            {items.map((item) => (
              <NotificationCard
                key={item.id}
                item={item}
                onSelect={() => openItem(item)}
              />
            ))}
          </Box>
        ) : null}
      </Box>
    </Screen>
  );
}
